// src/features/compliance/pages/SecurityScanningPage.tsx
// Security Scanning tab — backed by security-scan-service
// OpenAPI: POST /api/security-scan/submit/file  (SubmitFileServiceScanRequest)
//          POST /api/security-scan/submit/git    (SubmitGitScanRequest)
//          GET  /api/security-scan/scans          (list scans)
//          GET  /api/security-scan/scans/{id}     (get scan + findings)
//          GET  /api/security-scan/findings        (list findings)
//          PUT  /api/security-scan/findings/{id}   (update finding status)
//          GET  /api/security-scan/stats           (scan stats)
// © 2025–2026 MCX Services, LLC. All rights reserved.

import { useState } from 'react'
import {
    Play, AlertTriangle, CheckCircle2, XCircle, Upload, X, Shield,
    Eye, Loader2, RefreshCw, ArrowLeft, Search, Filter, ChevronDown,
    ChevronRight, Info,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { useProjects } from '@/features/projects/api/useProjects'
import { useAttachments } from '@/features/files/api/useFiles'
import {
    useSecurityScans, useSecurityScan, useScanStats,
    useSubmitFileScan, useUpdateFinding,
} from '../api/useSecurityScans'
import type { SecurityScan, SecurityFinding, SubmitFileScanRequest, UpdateFindingRequest } from '../api/useSecurityScans'
import { toast } from 'sonner'
import { apiClient } from '@/lib/api/client'

// ============================================================================
// Constants
// ============================================================================

const ACTIVE_STATUSES = new Set(['pending', 'queued', 'running', 'in_progress', 'processing'])

const LANGUAGE_OPTIONS = [
    { value: 'python', label: 'Python' },
    { value: 'javascript', label: 'JavaScript' },
    { value: 'typescript', label: 'TypeScript' },
    { value: 'java', label: 'Java' },
    { value: 'csharp', label: 'C#' },
    { value: 'cpp', label: 'C / C++' },
    { value: 'go', label: 'Go' },
    { value: 'rust', label: 'Rust' },
    { value: 'ruby', label: 'Ruby' },
    { value: 'php', label: 'PHP' },
    { value: 'swift', label: 'Swift' },
    { value: 'kotlin', label: 'Kotlin' },
    { value: 'scala', label: 'Scala' },
    { value: 'sql', label: 'SQL' },
    { value: 'cobol', label: 'COBOL' },
    { value: 'other', label: 'Other' },
] as const

const SCAN_TYPE_OPTIONS = [
    { value: 'cve', label: 'CVE Detection', description: 'Known vulnerability database matching' },
    { value: 'complexity', label: 'Complexity Analysis', description: 'Cyclomatic and cognitive complexity' },
    { value: 'fragility', label: 'Fragility Analysis', description: 'Code fragility and maintainability' },
] as const

const FINDING_STATUSES = ['open', 'acknowledged', 'remediated', 'false_positive', 'wont_fix'] as const

// ============================================================================
// Helpers
// ============================================================================

const SEVERITY_CFG: Record<string, { color: string; bg: string; icon: React.ReactNode }> = {
    critical: { color: 'text-red-700',    bg: 'bg-red-50 border-red-200',       icon: <XCircle className="h-3.5 w-3.5" /> },
    high:     { color: 'text-orange-700', bg: 'bg-orange-50 border-orange-200', icon: <AlertTriangle className="h-3.5 w-3.5" /> },
    medium:   { color: 'text-yellow-700', bg: 'bg-yellow-50 border-yellow-200', icon: <AlertTriangle className="h-3.5 w-3.5" /> },
    low:      { color: 'text-blue-700',   bg: 'bg-blue-50 border-blue-200',     icon: <Info className="h-3.5 w-3.5" /> },
}

function SeverityBadge({ severity }: { severity: string }) {
    const cfg = SEVERITY_CFG[severity.toLowerCase()] ?? SEVERITY_CFG.low
    return (
        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${cfg.bg} ${cfg.color}`}>
      {cfg.icon}{severity.charAt(0).toUpperCase() + severity.slice(1)}
    </span>
    )
}

function StatusBadge({ status }: { status: string }) {
    const isActive = ACTIVE_STATUSES.has(status)
    if (status === 'completed') return <Badge className="bg-green-600 hover:bg-green-700">Completed</Badge>
    if (status === 'failed') return <Badge variant="destructive">Failed</Badge>
    if (isActive) return (
        <Badge variant="secondary" className="flex items-center gap-1 w-fit">
            <Loader2 className="h-3 w-3 animate-spin" />
            {status === 'queued' ? 'Queued' : status === 'processing' ? 'Processing' : 'Pending'}
        </Badge>
    )
    return <Badge variant="outline">{status}</Badge>
}

function formatDate(iso: string | null | undefined): string {
    if (!iso) return '—'
    try {
        return new Date(iso).toLocaleString(undefined, {
            month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
        })
    } catch { return iso }
}

// ============================================================================
// FindingRow — expandable detail with inline status update
// ============================================================================

function FindingRow({ finding }: { finding: SecurityFinding }) {
    const [open, setOpen] = useState(false)
    const updateFinding = useUpdateFinding()

    const handleStatusChange = (newStatus: string) => {
        updateFinding.mutate(
            { findingId: finding.id, update: { status: newStatus as UpdateFindingRequest['status'] } },
            { onError: () => toast.error('Failed to update finding status') },
        )
    }

    return (
        <>
            <TableRow className="cursor-pointer hover:bg-gray-50" onClick={() => setOpen(p => !p)}>
                <TableCell className="w-8">
                    {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </TableCell>
                <TableCell><SeverityBadge severity={finding.severity} /></TableCell>
                <TableCell className="text-sm font-medium">{finding.finding_type}</TableCell>
                <TableCell className="max-w-xs truncate text-sm" title={finding.title}>{finding.title}</TableCell>
                <TableCell className="font-mono text-xs text-gray-500">
                    {finding.file_path ?? '—'}{finding.line_number != null ? `:${finding.line_number}` : ''}
                </TableCell>
                <TableCell>
                    {finding.cve_id && <Badge variant="outline" className="mr-1 text-xs">{finding.cve_id}</Badge>}
                    {finding.cwe_id && <Badge variant="outline" className="text-xs">{finding.cwe_id}</Badge>}
                </TableCell>
                <TableCell onClick={e => e.stopPropagation()}>
                    <Select value={finding.status} onValueChange={handleStatusChange} disabled={updateFinding.isPending}>
                        <SelectTrigger className="h-7 w-36 text-xs">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {FINDING_STATUSES.map(s => (
                                <SelectItem key={s} value={s} className="text-xs">
                                    {s.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </TableCell>
            </TableRow>

            {open && (
                <TableRow>
                    <TableCell colSpan={7} className="bg-gray-50 p-4">
                        <div className="space-y-3 text-sm">
                            <div>
                                <p className="font-semibold">Description</p>
                                <p className="text-gray-600">{finding.description}</p>
                            </div>
                            {finding.remediation && (
                                <div>
                                    <p className="font-semibold">Remediation</p>
                                    <p className="whitespace-pre-wrap text-gray-600">{finding.remediation}</p>
                                </div>
                            )}
                            <div className="flex flex-wrap gap-4 text-xs text-gray-400">
                                <span>Confidence: <strong>{Math.round((finding as any).confidence ?? 75) * 100}%</strong></span>
                                {(finding as any).source && <span>Source: <strong>{(finding as any).source}</strong></span>}
                            </div>
                        </div>
                    </TableCell>
                </TableRow>
            )}
        </>
    )
}

// ============================================================================
// SecurityScanDetail — replaces form+history when a scan is selected
// ============================================================================

function SecurityScanDetail({
                                scanId,
                                onBack,
                                onRefresh,
                            }: {
    scanId: string
    onBack: () => void
    onRefresh: () => void
}) {
    const [sevFilter, setSevFilter] = useState('all')
    const [typeFilter, setTypeFilter] = useState('all')
    const [statusFilter, setStatusFilter] = useState('all')
    const [search, setSearch] = useState('')

    const { data: scan, isLoading } = useSecurityScan(scanId)

    const isActive = scan ? ACTIVE_STATUSES.has(scan.status) : false
    const isTerminal = scan ? (scan.status === 'completed' || scan.status === 'failed') : false

    const allFindings = scan?.findings ?? []

    const findingTypes = [...new Set(allFindings.map(f => f.finding_type))]

    const findings = allFindings.filter(f => {
        if (sevFilter !== 'all' && f.severity.toLowerCase() !== sevFilter) return false
        if (typeFilter !== 'all' && f.finding_type !== typeFilter) return false
        if (statusFilter !== 'all' && f.status !== statusFilter) return false
        if (search) {
            const q = search.toLowerCase()
            return (
                f.title.toLowerCase().includes(q) ||
                f.finding_type.toLowerCase().includes(q) ||
                (f.file_path ?? '').toLowerCase().includes(q) ||
                (f.description ?? '').toLowerCase().includes(q)
            )
        }
        return true
    })

    const sevCounts = allFindings.reduce<Record<string, number>>((acc, f) => {
        const k = f.severity.toLowerCase()
        acc[k] = (acc[k] ?? 0) + 1
        return acc
    }, {}

   )

   void sevCounts

    return (
        <div className="space-y-6">
            {/* ── Header ── */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="sm" onClick={onBack}>
                        <ArrowLeft className="mr-2 h-4 w-4" />Back
                    </Button>
                    <div className="h-5 w-px bg-gray-200" />
                    <div>
                        <h2 className="text-lg font-semibold">Scan Results</h2>
                        <p className="font-mono text-xs text-gray-400">{scanId.slice(0, 8)}…</p>
                    </div>
                </div>
                {!isTerminal && (
                    <Button variant="outline" size="sm" onClick={onRefresh}>
                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />Refreshing…
                    </Button>
                )}
            </div>

            {/* ── Loading state ── */}
            {isLoading && (
                <div className="flex items-center justify-center py-16">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
            )}

            {/* ── Summary cards ── */}
            {scan && (
                <>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {/* Status */}
                        <Card>
                            <CardContent className="space-y-3 pt-6">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-500">Status</span>
                                    <StatusBadge status={scan.status} />
                                </div>
                                <div className="h-px bg-gray-100" />
                                <div className="space-y-1 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Total Findings</span>
                                        <span className="font-semibold">{scan.total_findings}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">Language</span>
                                        <span className="font-medium capitalize">{scan.language}</span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Severity breakdown */}
                        <Card>
                            <CardContent className="space-y-2 pt-6">
                                <p className="text-sm font-medium">Findings by Severity</p>
                                {(['critical', 'high', 'medium', 'low'] as const).map(sev => (
                                    <div key={sev} className="flex items-center gap-2">
                                        <SeverityBadge severity={sev} />
                                        <span className="ml-auto font-mono text-sm font-semibold">
                      {sev === 'critical' ? scan.critical_count
                          : sev === 'high' ? scan.high_count
                              : sev === 'medium' ? scan.medium_count
                                  : scan.low_count}
                    </span>
                                    </div>
                                ))}
                            </CardContent>
                        </Card>

                        {/* Scan types */}
                        <Card>
                            <CardContent className="space-y-2 pt-6">
                                <p className="text-sm font-medium">Scan Types</p>
                                <div className="flex flex-wrap gap-1">
                                    {scan.scan_types?.map(t => (
                                        <Badge key={t} variant="outline" className="text-xs">{t}</Badge>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Timestamps */}
                        <Card>
                            <CardContent className="space-y-1 pt-6 text-xs text-gray-400">
                                <p className="text-sm font-medium text-gray-700 mb-2">Timeline</p>
                                <div>Started: {formatDate(scan.created_at)}</div>
                                {scan.completed_at && <div>Completed: {formatDate(scan.completed_at)}</div>}
                            </CardContent>
                        </Card>
                    </div>

                    {/* ── Active scan notice ── */}
                    {isActive && (
                        <Alert>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <AlertDescription>
                                Scan is in progress — findings will populate automatically as analysis completes.
                            </AlertDescription>
                        </Alert>
                    )}

                    {/* ── Findings table ── */}
                    {allFindings.length > 0 && (
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="flex items-center gap-2 text-base">
                                    <Shield className="h-4 w-4" />Findings ({findings.length})
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                {/* Filters */}
                                <div className="flex flex-wrap gap-2">
                                    <div className="relative">
                                        <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400" />
                                        <Input
                                            placeholder="Search…"
                                            className="h-8 w-56 pl-8 text-sm"
                                            value={search}
                                            onChange={e => setSearch(e.target.value)}
                                        />
                                    </div>
                                    <Select value={sevFilter} onValueChange={setSevFilter}>
                                        <SelectTrigger className="h-8 w-40 text-sm">
                                            <Filter className="mr-1.5 h-3 w-3" /><SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">All Severities</SelectItem>
                                            <SelectItem value="critical">Critical</SelectItem>
                                            <SelectItem value="high">High</SelectItem>
                                            <SelectItem value="medium">Medium</SelectItem>
                                            <SelectItem value="low">Low</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <Select value={typeFilter} onValueChange={setTypeFilter}>
                                        <SelectTrigger className="h-8 w-44 text-sm"><SelectValue placeholder="Finding Type" /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">All Types</SelectItem>
                                            {findingTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                                        <SelectTrigger className="h-8 w-40 text-sm"><SelectValue placeholder="Status" /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">All Statuses</SelectItem>
                                            {FINDING_STATUSES.map(s => (
                                                <SelectItem key={s} value={s}>
                                                    {s.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Table */}
                                <div className="overflow-hidden rounded-md border">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead className="w-8" />
                                                <TableHead>Severity</TableHead>
                                                <TableHead>Type</TableHead>
                                                <TableHead>Title</TableHead>
                                                <TableHead>Location</TableHead>
                                                <TableHead>CVE / CWE</TableHead>
                                                <TableHead>Status</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {findings.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={7} className="py-8 text-center text-sm text-gray-400">
                                                        No findings match the current filters.
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                findings.map(f => <FindingRow key={f.id} finding={f} />)
                                            )}
                                        </TableBody>
                                    </Table>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* ── Clean scan ── */}
                    {scan.status === 'completed' && allFindings.length === 0 && (
                        <div className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 p-4 text-green-800">
                            <CheckCircle2 className="h-6 w-6" />
                            <div>
                                <p className="font-semibold">No vulnerabilities detected</p>
                                <p className="text-sm text-green-700">All scan checks passed cleanly.</p>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    )
}

// ============================================================================
// SecurityScanningPage
// ============================================================================

export default function SecurityScanningPage() {
    // ── Selected scan — when set, detail view takes over ────────
    const [selectedScanId, setSelectedScanId] = useState<string | null>(null)

    // ── Form state ──────────────────────────────────────────────
    const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null)
    const [language, setLanguage] = useState('')
    const [selectedScanTypes, setSelectedScanTypes] = useState<string[]>(['cve', 'complexity', 'fragility'])
    const [scanFile, setScanFile] = useState<File | null>(null)
    const [useExistingFile, setUseExistingFile] = useState(false)
    const [existingFileId, setExistingFileId] = useState<string>('')
    const [isUploading, setIsUploading] = useState(false)

    // ── Queries ─────────────────────────────────────────────────
    const { data: projects } = useProjects()
    const { data: existingAttachments } = useAttachments()
    const {
        data: scansData,
        isLoading: scansLoading,
        isError: scansError,
        isRefetching: scansRefetching,
        refetch: refetchScans,
    } = useSecurityScans(selectedProjectId ?? undefined)
    const { data: stats } = useScanStats(selectedProjectId ?? undefined)
    const submitFileScan = useSubmitFileScan()

    const uniqueAttachments = existingAttachments
        ? Array.from(new Map(existingAttachments.map(a => [a.id, a])).values())
        : []
    const scans = scansData?.scans ?? []
    const hasActiveScans = scans.some(s => ACTIVE_STATUSES.has(s.status))
    const showPollingIndicator = hasActiveScans && scansRefetching

    // ── If a scan is selected, render detail view ────────────────
    if (selectedScanId) {
        return (
            <SecurityScanDetail
                scanId={selectedScanId}
                onBack={() => setSelectedScanId(null)}
                onRefresh={refetchScans}
            />
        )
    }

    // ── Handlers ────────────────────────────────────────────────

    const toggleScanType = (type: string) =>
        setSelectedScanTypes(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type])

    const handleRunScan = async () => {
        if (!selectedProjectId) { toast.error('Please select a project'); return }
        if (!language) { toast.error('Please select a programming language'); return }
        if (selectedScanTypes.length === 0) { toast.error('Please select at least one scan type'); return }
        if (!useExistingFile && !scanFile) { toast.error('Please upload a codeset file or select an existing one'); return }
        if (useExistingFile && !existingFileId) { toast.error('Please select an existing file'); return }

        try {
            let fileUploadId: number
            if (useExistingFile) {
                fileUploadId = parseInt(existingFileId, 10)
            } else {
                setIsUploading(true)
                const formData = new FormData()
                formData.append('file', scanFile!)
                formData.append('fk_table', 'security_scan')
                formData.append('fk_id', selectedProjectId.toString())
                const uploadResponse = await apiClient.post('/api/attachments', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                })
                fileUploadId = uploadResponse.data.id
                setIsUploading(false)
            }

            const request: SubmitFileScanRequest = {
                project_id: selectedProjectId,
                file_upload_id: fileUploadId,
                language,
                scan_types: selectedScanTypes as Array<'cve' | 'complexity' | 'fragility'>,
            }

            const result = await submitFileScan.mutateAsync(request)
            toast.success('Security scan submitted')

            // Navigate immediately to detail — spinning refresh button confirms results are incoming
            const newScanId = result?.id ?? result?.scan_id
            if (newScanId) setSelectedScanId(newScanId)

            setScanFile(null)
            setExistingFileId('')
        } catch (error: any) {
            toast.error(error?.response?.data?.detail || 'Failed to submit security scan')
        } finally {
            setIsUploading(false)
        }
    }

    const isSubmitting = submitFileScan.isPending || isUploading
    const canSubmit =
        !!selectedProjectId && !!language && selectedScanTypes.length > 0 &&
        ((!useExistingFile && !!scanFile) || (useExistingFile && !!existingFileId)) && !isSubmitting

    // ── Render list/form view ────────────────────────────────────

    return (
        <div className="space-y-6">
            {/* ── Stats Summary ── */}
            {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card>
                        <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Total Scans</CardTitle></CardHeader>
                        <CardContent><div className="text-2xl font-bold">{stats.total_scans}</div></CardContent>
                    </Card>
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium flex items-center gap-1">
                                <XCircle className="h-3 w-3 text-red-800" /> Critical
                            </CardTitle>
                        </CardHeader>
                        <CardContent><div className="text-2xl font-bold text-red-800">{stats.critical_count}</div></CardContent>
                    </Card>
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium flex items-center gap-1">
                                <XCircle className="h-3 w-3 text-red-600" /> High
                            </CardTitle>
                        </CardHeader>
                        <CardContent><div className="text-2xl font-bold text-red-600">{stats.high_count}</div></CardContent>
                    </Card>
                    <Card>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-sm font-medium flex items-center gap-1">
                                <AlertTriangle className="h-3 w-3 text-yellow-600" /> Medium / Low
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold text-yellow-600">{stats.medium_count + stats.low_count}</div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* ── Submit Scan Form ── */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2"><Play className="h-5 w-5" />Run Security Scan</CardTitle>
                    <CardDescription>
                        Upload a codeset archive or select an existing file to scan for vulnerabilities
                        against CVE databases with AI-powered remediation
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Project <span className="text-red-500">*</span></Label>
                            <Select value={selectedProjectId?.toString() ?? ''} onValueChange={v => setSelectedProjectId(parseInt(v))}>
                                <SelectTrigger><SelectValue placeholder="Select a project" /></SelectTrigger>
                                <SelectContent>
                                    {projects?.map(p => <SelectItem key={p.id} value={p.id.toString()}>{p.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Language <span className="text-red-500">*</span></Label>
                            <Select value={language} onValueChange={setLanguage}>
                                <SelectTrigger><SelectValue placeholder="Select primary language" /></SelectTrigger>
                                <SelectContent>
                                    {LANGUAGE_OPTIONS.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label>Scan Types <span className="text-red-500">*</span></Label>
                        <div className="flex flex-wrap gap-4">
                            {SCAN_TYPE_OPTIONS.map(st => (
                                <div key={st.value} className="flex items-start gap-2">
                                    <Checkbox
                                        id={`scan-type-${st.value}`}
                                        checked={selectedScanTypes.includes(st.value)}
                                        onCheckedChange={() => toggleScanType(st.value)}
                                    />
                                    <div>
                                        <Label htmlFor={`scan-type-${st.value}`} className="cursor-pointer font-medium">{st.label}</Label>
                                        <p className="text-xs text-muted-foreground">{st.description}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                            <Checkbox
                                id="use-existing-security-file"
                                checked={useExistingFile}
                                onCheckedChange={checked => {
                                    setUseExistingFile(checked as boolean)
                                    if (checked) setScanFile(null)
                                    else setExistingFileId('')
                                }}
                            />
                            <Label htmlFor="use-existing-security-file" className="text-sm font-normal cursor-pointer">
                                Use previously uploaded file
                            </Label>
                        </div>

                        {useExistingFile ? (
                            <div className="space-y-2">
                                <Label>Select Existing File <span className="text-red-500">*</span></Label>
                                <Select value={existingFileId} onValueChange={setExistingFileId}>
                                    <SelectTrigger><SelectValue placeholder="Select a file" /></SelectTrigger>
                                    <SelectContent>
                                        {uniqueAttachments.length > 0 ? (
                                            uniqueAttachments.map(att => (
                                                <SelectItem key={att.id} value={att.id.toString()}>
                                                    {att.file_name} ({(att.file_size / 1024 / 1024).toFixed(2)} MB)
                                                </SelectItem>
                                            ))
                                        ) : (
                                            <SelectItem value="none" disabled>No files available</SelectItem>
                                        )}
                                    </SelectContent>
                                </Select>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <Label>Codeset Archive <span className="text-red-500">*</span></Label>
                                <div className="flex items-center gap-2">
                                    <Input
                                        type="file"
                                        accept=".zip,.tar,.tar.gz,.tgz"
                                        onChange={e => setScanFile(e.target.files?.[0] || null)}
                                        className="flex-1"
                                    />
                                    {scanFile && (
                                        <Button variant="ghost" size="sm" onClick={() => setScanFile(null)}>
                                            <X className="h-4 w-4" />
                                        </Button>
                                    )}
                                </div>
                                <p className="text-xs text-muted-foreground">Supported: .zip, .tar, .tar.gz</p>
                            </div>
                        )}
                    </div>

                    <Button onClick={handleRunScan} disabled={!canSubmit} className="w-full" size="lg">
                        {isSubmitting ? (
                            <><Loader2 className="mr-2 h-4 w-4 animate-spin" />{isUploading ? 'Uploading file…' : 'Submitting scan…'}</>
                        ) : (
                            <><Upload className="mr-2 h-4 w-4" />Run Security Scan</>
                        )}
                    </Button>
                </CardContent>
            </Card>

            {/* ── Scan History ── */}
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div>
                            <CardTitle className="flex items-center gap-2">
                                <Shield className="h-5 w-5" />
                                Scan History
                                {showPollingIndicator && (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 border border-blue-200">
                    <RefreshCw className="h-3 w-3 animate-spin" />Refreshing
                  </span>
                                )}
                                {hasActiveScans && !showPollingIndicator && (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground border">
                    <span className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse" />Scanning
                  </span>
                                )}
                            </CardTitle>
                            <CardDescription className="mt-1">
                                Previous security scans {selectedProjectId ? 'for this project' : 'across all projects'}
                            </CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {scansLoading ? (
                        <div className="space-y-2">
                            {Array.from({ length: 3 }).map((_, i) => (
                                <div key={i} className="h-12 bg-muted animate-pulse rounded" />
                            ))}
                        </div>
                    ) : scans.length === 0 && scansError ? (
                        <Alert variant="destructive">
                            <AlertTriangle className="h-4 w-4" />
                            <AlertDescription>
                                Unable to load scan history. The security-scan service may be unavailable.
                            </AlertDescription>
                        </Alert>
                    ) : scans.length === 0 ? (
                        <div className="text-center py-10">
                            <Shield className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                            <p className="text-muted-foreground">No scans found</p>
                            <p className="text-sm text-muted-foreground mt-1">Configure and submit a scan above to get started</p>
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Scan ID</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Language</TableHead>
                                    <TableHead>Types</TableHead>
                                    <TableHead className="text-right">Critical</TableHead>
                                    <TableHead className="text-right">High</TableHead>
                                    <TableHead className="text-right">Medium</TableHead>
                                    <TableHead className="text-right">Low</TableHead>
                                    <TableHead className="text-right">Total</TableHead>
                                    <TableHead>Created</TableHead>
                                    <TableHead />
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {scans.map((scan: SecurityScan) => (
                                    <TableRow
                                        key={scan.id}
                                        className="cursor-pointer hover:bg-gray-50"
                                        onClick={() => setSelectedScanId(scan.id)}
                                    >
                                        <TableCell className="font-mono text-xs">{scan.id.slice(0, 8)}…</TableCell>
                                        <TableCell><StatusBadge status={scan.status} /></TableCell>
                                        <TableCell className="capitalize">{scan.language}</TableCell>
                                        <TableCell>
                                            <div className="flex gap-1 flex-wrap">
                                                {scan.scan_types?.map(t => <Badge key={t} variant="outline" className="text-xs">{t}</Badge>)}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-right font-bold text-red-800">{scan.critical_count || 0}</TableCell>
                                        <TableCell className="text-right font-bold text-red-600">{scan.high_count || 0}</TableCell>
                                        <TableCell className="text-right text-yellow-600">{scan.medium_count || 0}</TableCell>
                                        <TableCell className="text-right text-muted-foreground">{scan.low_count || 0}</TableCell>
                                        <TableCell className="text-right font-semibold">{scan.total_findings}</TableCell>
                                        <TableCell className="text-sm whitespace-nowrap">{formatDate(scan.created_at)}</TableCell>
                                        <TableCell>
                                            <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); setSelectedScanId(scan.id) }}>
                                                <Eye className="h-4 w-4" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </div>
    )
}
